import React, { useState, useEffect } from 'react';
import { User, X, Settings, LogOut, Shield, Calendar, Bell, CreditCard, BarChart } from 'lucide-react';
import axios from 'axios'; 
import Avatar from '../../../services/Avatar';

const UserProfile = ({ user, onClose, onSettingsChange }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const formatLastActive = (date) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(date).toLocaleString(undefined, options);
  };

 
     
    if (!user) {
    return <div>User not found.</div>; // Handle case when user is still null
    }
  
  const handleLogout = async () => {
    try {
      const response = await fetch('/d-auth/logout', {
        method: 'POST', // or 'GET', depending on your backend implementation
        credentials: 'include', // include cookies with the request
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok) {       
        
        window.location.href = '/'; 
      } else {
        // If there's an error during logout
        const errorData = await response.json();
        //console.error('Logout failed', errorData);
        alert( errorData ||'Logout failed. Please try again.');
      }
    } catch (error) {
      //console.error('Error during logout:', error);
      alert('An error occurred. Please try again.');
    }
  };
  
  const securityLevelColor = {
    'Basic': 'text-yellow-500',
    'Advanced': 'text-green-600',
    'Enterprise': 'text-purple-600'
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl h-auto max-h-[90vh] overflow-y-auto shadow-xl relative">
        {/* Header with close button */}
        <div className="relative p-6 pb-3 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close profile"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center">
            {user.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt={user.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-blue-500"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <Avatar firstName={user.first_name} lastName={user.last_name} />   
              </div>
            )}
            
            <div className="ml-4">
              <h2 className="text-xl font-bold dark:text-white flex items-center">
                {user.username}
                {user.verified && (
                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full dark:bg-green-900 dark:text-green-100">
                    Verified
                  </span>
                )}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">{user.role || 'User'}</p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            className={`flex-1 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'overview' 
                ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`flex-1 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'security' 
                ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
          <button
            className={`flex-1 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'activity' 
                ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Username</span>
                </div>
                <span className={`font-semibold ${securityLevelColor[user.username || '']}`}>
                  {user.username || ''}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Last Active</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300 text-sm">
                  {formatLastActive(user.last_login || new Date())}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <BarChart className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Account Status</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300">
                  {user.is_active || 'Active'}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Notifications</span>
                </div>
                <div className="relative inline-block w-10 ml-2 align-middle">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    id="notifications"
                    defaultChecked={user.notificationsEnabled} 
                  />
                  <label 
                    htmlFor="notifications"
                    className="block h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer transition-colors"
                  >
                    <span 
                      className="block h-4 w-4 mt-1 ml-1 bg-white rounded-full transform transition-transform peer-checked:translate-x-4"
                    ></span>
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'security' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Two-Factor Auth</span>
                </div>
                <div className="relative inline-block w-10 ml-2 align-middle">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    id="twoFA"
                    defaultChecked={user.twoFactorEnabled} 
                  />
                  <label 
                    htmlFor="twoFA"
                    className="block h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer transition-colors"
                  >
                    <span 
                      className="block h-4 w-4 mt-1 ml-1 bg-white rounded-full transform transition-transform peer-checked:translate-x-4"
                    ></span>
                  </label>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">Password Updated</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300 text-sm">
                  {user.passwordUpdated ? new Date(user.passwordUpdated).toLocaleDateString() : 'Never'}
                </span>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {(user.recentActivity || [
                { type: 'login', date: new Date(Date.now() - 3600000) },
                { type: 'settings_change', date: new Date(Date.now() - 86400000) },
                { type: 'password_reset', date: new Date(Date.now() - 604800000) }
              ]).map((activity, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {activity.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      {formatLastActive(activity.date)}
                    </span>
                  </div>
                  {activity.details && (
                    <p className="mt-1 text-gray-500 dark:text-gray-400">{activity.details}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer with action buttons */}
        <div className="p-6 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-3">
            <button
              className="flex-1 bg-white border border-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={() => onSettingsChange && onSettingsChange()}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>

  );
};

export default UserProfile;